import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  type PeriodGranularity,
  periodForNow,
  periodLabel,
  shiftPeriod,
} from "@/lib/dashboard-period";
import { usePeriodFilter } from "@/contexts/dashboard-filter-context";

const GRANULARITIES: { value: PeriodGranularity; label: string }[] = [
  { value: "month", label: "Måned" },
  { value: "quarter", label: "Kvartal" },
  { value: "year", label: "År" },
  { value: "custom", label: "Egendefinert" },
];

/**
 * Period picker for the sales dashboard (F0.2). Granularity toggle + prev/next
 * stepping (or two date inputs for custom). Drives PeriodContext so every panel
 * re-filters at once.
 */
export function PeriodSelector() {
  const { period, setPeriod, bounds } = usePeriodFilter();

  function changeGranularity(next: PeriodGranularity) {
    // Re-anchor to the period covering today at the new granularity.
    setPeriod(periodForNow(next, new Date()));
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <ToggleGroup
        type="single"
        value={period.granularity}
        onValueChange={(v) => v && changeGranularity(v as PeriodGranularity)}
        aria-label="Velg periodetype"
      >
        {GRANULARITIES.map((g) => (
          <ToggleGroupItem key={g.value} value={g.value} aria-label={g.label}>
            {g.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {period.granularity === "custom" ? (
        <div className="flex items-center gap-2">
          <Input
            type="date"
            aria-label="Fra dato"
            value={bounds.fromDate}
            max={bounds.toDate}
            onChange={(e) =>
              setPeriod({ ...period, customFrom: e.target.value, customTo: bounds.toDate })
            }
            className="w-40"
          />
          <span className="text-muted-foreground">–</span>
          <Input
            type="date"
            aria-label="Til dato"
            value={bounds.toDate}
            min={bounds.fromDate}
            onChange={(e) =>
              setPeriod({ ...period, customFrom: bounds.fromDate, customTo: e.target.value })
            }
            className="w-40"
          />
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            aria-label="Forrige periode"
            onClick={() => setPeriod(shiftPeriod(period, -1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[7rem] text-center font-medium tabular-nums">
            {periodLabel(period)}
          </span>
          <Button
            variant="outline"
            size="icon"
            aria-label="Neste periode"
            onClick={() => setPeriod(shiftPeriod(period, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
